import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { getBrowserDateMaskConfig, getDateMaskConfig, isValidDate } from 'garaje/utils/date-format';
import type { DateFieldFormat } from 'garaje/utils/enums';
import IMask from 'imask';

export interface DateInputSignature {
  Args: {
    value?: string;
    format?: DateFieldFormat;
    disabled?: boolean;
    required?: boolean;
    placeholder?: string;
    id?: string;
    class?: string;
    showError?: boolean;
    onChange?: (value: string, isValid: boolean) => void;
    onBlur?: (value: string, isValid: boolean) => void;
  };
  Element: HTMLDivElement;
}

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export default class DateInput extends Component<DateInputSignature> {
  @tracked inputValue = '';
  @tracked isValid = false;
  @tracked rejectionError = '';
  @tracked partialError = '';

  #elementId = guidFor(this);

  private imaskInstance?: ReturnType<typeof IMask>;
  private lastSyncedValue?: string;
  private errorTimeout?: ReturnType<typeof setTimeout>;

  get elementId(): string {
    return this.args.id ?? this.#elementId;
  }

  get maskConfig(): ReturnType<typeof getDateMaskConfig> {
    if (this.args.format) {
      return getDateMaskConfig(this.args.format);
    }
    return getBrowserDateMaskConfig();
  }

  get placeholderText(): string {
    return this.args.placeholder ?? this.maskConfig.placeholder;
  }

  get isComplete(): boolean {
    return !this.inputValue.includes('_') && this.inputValue.length > 0;
  }

  get showValidationError(): boolean {
    if (this.args.showError) return true;
    if (this.rejectionError) return true;
    if (this.partialError) return true;
    if (this.isComplete && !this.isValid) return true;
    return false;
  }

  get errorMessage(): string {
    if (this.rejectionError) {
      return this.rejectionError;
    }
    if (this.partialError) {
      return this.partialError;
    }
    if (this.isComplete && !this.isValid) {
      return 'Invalid date';
    }
    return '';
  }

  get inputClasses(): string {
    const baseClass = this.args.class ?? '';
    const errorClass = this.showValidationError ? 'error border-red-500' : '';
    return `${baseClass} ${errorClass}`.trim();
  }

  validateDate(value: string): boolean {
    const parsed = this.maskConfig.parse(value);
    if (!parsed) {
      return false;
    }
    return isValidDate(parsed.day, parsed.month, parsed.year);
  }

  private extractPartialDate(value: string): { month?: number; day?: number; year?: number } {
    const mask = this.maskConfig.mask;
    const result: { month?: number; day?: number; year?: number } = {};

    const blockRegex = /MM|DD|YYYY/g;
    let match;

    while ((match = blockRegex.exec(mask)) !== null) {
      const blockName = match[0];
      const startPos = match.index;
      const length = blockName.length;
      const valuePart = value.substring(startPos, startPos + length);

      if (!valuePart || valuePart.length < length || valuePart.includes('_')) {
        continue;
      }

      const num = parseInt(valuePart, 10);
      if (isNaN(num)) continue;

      if (blockName === 'MM') result.month = num;
      else if (blockName === 'DD') result.day = num;
      else if (blockName === 'YYYY') result.year = num;
    }

    return result;
  }

  private validatePartialDate(value: string): string | null {
    const { month, day, year } = this.extractPartialDate(value);

    if (month !== undefined && day !== undefined) {
      const maxDays = DAYS_IN_MONTH[month - 1];

      if (maxDays === undefined) {
        return null;
      }

      if (month === 2 && year !== undefined) {
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        const febDays = isLeapYear ? 29 : 28;

        if (day > febDays) {
          return isLeapYear ? 'February has max 29 days in leap year' : `February has max 28 days in ${year}`;
        }
      } else if (month === 2 && day > 29) {
        return 'February has max 29 days';
      } else if (day > maxDays) {
        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
        return `${monthNames[month - 1]} has max ${maxDays} days`;
      }
    }

    return null;
  }

  private clearErrorTimeout(): void {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = undefined;
    }
  }

  private showTemporaryError(message: string): void {
    this.clearErrorTimeout();
    this.rejectionError = message;

    this.errorTimeout = setTimeout(() => {
      this.rejectionError = '';
    }, 2000);
  }

  setupIMask = modifier((element: HTMLInputElement, [format]: [DateFieldFormat | undefined]) => {
    const config = format ? getDateMaskConfig(format) : getBrowserDateMaskConfig();

    this.imaskInstance = IMask(element, {
      mask: config.mask,
      blocks: config.blocks,
      lazy: false,
      placeholderChar: '_',
    });

    this.inputValue = this.imaskInstance.value;
    this.lastSyncedValue = this.inputValue;

    this.imaskInstance.on('accept', () => {
      this.clearErrorTimeout();
      this.rejectionError = '';

      const currentValue = this.imaskInstance?.value ?? '';
      this.inputValue = currentValue;
      this.isValid = this.validateDate(currentValue);
      this.lastSyncedValue = currentValue;

      const partialValidationError = this.validatePartialDate(currentValue);
      this.partialError = partialValidationError ?? '';

      this.args.onChange?.(currentValue, this.isValid && !partialValidationError);
    });

    const handleBeforeInput = (e: InputEvent) => {
      if (e.data && /\d/.test(e.data)) {
        const valueBefore = this.imaskInstance?.value ?? '';

        setTimeout(() => {
          const valueAfter = this.imaskInstance?.value ?? '';

          if (valueBefore === valueAfter && e.data) {
            const cursorPos = element.selectionStart ?? 0;
            const maskFormat = config.mask;

            if (maskFormat === 'MM/DD/YYYY') {
              if (cursorPos <= 2) {
                this.showTemporaryError('Month must be 01-12');
              } else if (cursorPos <= 5) {
                this.showTemporaryError('Day must be 01-31');
              } else {
                this.showTemporaryError('Year must be 1900-2100');
              }
            } else if (maskFormat === 'DD/MM/YYYY') {
              if (cursorPos <= 2) {
                this.showTemporaryError('Day must be 01-31');
              } else if (cursorPos <= 5) {
                this.showTemporaryError('Month must be 01-12');
              } else {
                this.showTemporaryError('Year must be 1900-2100');
              }
            } else if (maskFormat === 'YYYY-MM-DD') {
              if (cursorPos <= 4) {
                this.showTemporaryError('Year must be 1900-2100');
              } else if (cursorPos <= 7) {
                this.showTemporaryError('Month must be 01-12');
              } else {
                this.showTemporaryError('Day must be 01-31');
              }
            } else if (maskFormat === 'DD.MM.YYYY') {
              if (cursorPos <= 2) {
                this.showTemporaryError('Day must be 01-31');
              } else if (cursorPos <= 5) {
                this.showTemporaryError('Month must be 01-12');
              } else {
                this.showTemporaryError('Year must be 1900-2100');
              }
            } else {
              this.showTemporaryError('Invalid input');
            }
          }
        }, 0);
      }
    };

    const handleInput = () => {
      setTimeout(() => {
        const currentValue = this.imaskInstance?.value ?? '';
        if (currentValue !== this.inputValue) {
          this.clearErrorTimeout();
          this.rejectionError = '';

          this.inputValue = currentValue;
          this.isValid = this.validateDate(currentValue);
          this.lastSyncedValue = currentValue;
          const partialValidationError = this.validatePartialDate(currentValue);
          this.partialError = partialValidationError ?? '';
        }
      }, 0);
    };

    const handleKeyDown = () => {
      this.clearErrorTimeout();
      this.rejectionError = '';
    };

    element.addEventListener('keydown', handleKeyDown);
    element.addEventListener('beforeinput', handleBeforeInput);
    element.addEventListener('input', handleInput);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
      element.removeEventListener('beforeinput', handleBeforeInput);
      element.removeEventListener('input', handleInput);
      this.clearErrorTimeout();
      this.imaskInstance?.destroy();
    };
  });

  syncExternalValue = modifier((_element: HTMLInputElement, [externalValue]: [string | undefined]) => {
    if (externalValue !== undefined && externalValue !== this.lastSyncedValue && this.imaskInstance) {
      this.imaskInstance.value = externalValue;
      this.inputValue = externalValue;
      this.isValid = this.validateDate(externalValue);
      this.lastSyncedValue = externalValue;

      const partialValidationError = this.validatePartialDate(externalValue);
      this.partialError = partialValidationError ?? '';
    }

    return () => {};
  });

  @action
  handleBlur(): void {
    this.args.onBlur?.(this.inputValue, this.isValid && !this.partialError);
  }

  <template>
    <div class="date-input-wrapper" ...attributes>
      <input
        type="text"
        inputmode="numeric"
        id={{this.elementId}}
        class={{this.inputClasses}}
        disabled={{@disabled}}
        required={{@required}}
        placeholder={{this.placeholderText}}
        autocomplete="off"
        aria-invalid={{if this.showValidationError "true"}}
        aria-describedby={{if this.showValidationError (concat this.elementId "-error")}}
        data-test-date-input
        data-test-date-invalid={{if this.showValidationError "true"}}
        {{this.setupIMask @format}}
        {{this.syncExternalValue @value}}
        {{on "blur" this.handleBlur}}
      />
      {{#if this.showValidationError}}
        <div id="{{this.elementId}}-error" class="bottom-error pt-1" data-test-date-error>
          {{this.errorMessage}}
        </div>
      {{/if}}
    </div>
  </template>
}
